import React from "react";
import {
  CalendarDays,
  History,
  LoaderCircle,
  RefreshCcw,
} from "lucide-react";
import useDispatchCaptureArrivalHighlight from "../hooks/useDispatchCaptureArrivalHighlight.js";
import {
  buildOrderImportCaptureKey,
  buildReassignmentCaptureKey,
  buildReportActionCaptureKey,
  DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import { publishDispatchCaptureArrival } from "../lib/dispatchCaptureStatusEvents.js";
import {
  fetchReassignmentDiagnosticsHistory,
  fetchReportActionCaptureHistory,
  fetchSollioUniagOrderImportHistory,
} from "../lib/systemConfigApi.js";
import {
  buildCombinedCaptureActivityItems,
  COMBINED_CAPTURE_ACTIVITY_PAGE_SIZE,
  COMBINED_CAPTURE_IMPORT_BATCH_SIZE,
  paginateCombinedCaptureActivityItems,
  resolveDiagnosticActivityKind,
  resolveRequiredImportBatchCount,
} from "../utils/combinedCaptureActivityHistory.js";
import {
  formatCaptureActivityGroupLabel,
  groupCaptureItemsByActivityDate,
} from "../utils/dispatchCaptureListGroups.js";
import { normalizeReassignmentDiagnosticsHistoryPayload } from "../utils/planningLastDiagnostics.js";
import { ReassignmentHistoryCard } from "./ReassignmentDiagnosticsPage.jsx";
import { ReportActionHistoryCard } from "./ReportActionCaptureHistory.jsx";
import { JournalCaptureCard } from "./JournalCaptureHistory.jsx";
import { fetchJournalCaptureHistory } from "../lib/productUpdatesApi.js";
import { OrderImportHistoryCard } from "./SollioUniagOrderImportHistory.jsx";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";

const EMPTY_ITEMS = [];
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);
function isDocumentVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

function normalizeImportTotal(payload, fallbackItems = []) {
  const total = Number(payload?.total);
  return Number.isFinite(total) && total >= 0 ? Math.trunc(total) : fallbackItems.length;
}

async function fetchPaginatedCaptureHistory(fetchPage, page) {
  const firstPayload = await fetchPage({
    page: 1,
    pageSize: COMBINED_CAPTURE_IMPORT_BATCH_SIZE,
  });
  const firstItems = Array.isArray(firstPayload?.items) ? firstPayload.items : [];
  const total = normalizeImportTotal(firstPayload, firstItems);
  const requiredBatchCount = resolveRequiredImportBatchCount({ importTotal: total, page });
  const remainingPayloads = requiredBatchCount > 1
    ? await Promise.all(Array.from({ length: requiredBatchCount - 1 }, (_, index) => (
      fetchPage({
        page: index + 2,
        pageSize: COMBINED_CAPTURE_IMPORT_BATCH_SIZE,
      })
    )))
    : [];
  return {
    items: [firstPayload, ...remainingPayloads].flatMap((payload) => (
      Array.isArray(payload?.items) ? payload.items : []
    )),
    total,
  };
}

function CombinedActivityCard({ activity, diagnosticHighlightedKeys, importHighlightedKeys, reportHighlightedKeys, journalHighlightedKeys }) {
  const diagnosticKey = activity.activityKind === "imports" || activity.activityKind === "reports"
    ? ""
    : buildReassignmentCaptureKey(activity.item);
  const isNew = Boolean(diagnosticHighlightedKeys[activity.activityKind]?.has(diagnosticKey));

  return (
    <div
      className="grid min-w-0 gap-1"
      data-combined-capture-activity-item="true"
      data-combined-capture-activity-kind={activity.activityKind}
    >
      {activity.activityKind === "imports" ? (
        <OrderImportHistoryCard
          item={activity.item}
          isNew={importHighlightedKeys.has(buildOrderImportCaptureKey(activity.item))}
        />
      ) : activity.activityKind === "journal" ? (
        <JournalCaptureCard item={activity.item} isNew={journalHighlightedKeys.has(buildReportActionCaptureKey(activity.item))} />
      ) : activity.activityKind === "reports" ? (
        <ReportActionHistoryCard
          item={activity.item}
          isNew={reportHighlightedKeys.has(buildReportActionCaptureKey(activity.item))}
        />
      ) : (
        <ReassignmentHistoryCard isNew={isNew} item={activity.item} />
      )}
    </div>
  );
}

export default function CombinedCaptureActivityHistory() {
  const [page, setPage] = React.useState(1);
  const [historyState, setHistoryState] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const requestSequenceRef = React.useRef(0);
  const loadingRequestSequenceRef = React.useRef(0);

  const loadHistory = React.useCallback(async ({ quiet = false } = {}) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    if (!quiet) {
      loadingRequestSequenceRef.current = requestSequence;
      setIsLoading(true);
      setLoadError("");
    }

    try {
      const [diagnosticsPayload, importHistory, reportHistory, journalHistory] = await Promise.all([
        fetchReassignmentDiagnosticsHistory(),
        fetchPaginatedCaptureHistory(fetchSollioUniagOrderImportHistory, page),
        fetchPaginatedCaptureHistory(fetchReportActionCaptureHistory, page),
        fetchPaginatedCaptureHistory(fetchJournalCaptureHistory, page),
      ]);

      if (requestSequence !== requestSequenceRef.current) {
        return;
      }

      const diagnosticsState = normalizeReassignmentDiagnosticsHistoryPayload(diagnosticsPayload);
      setHistoryState({
        page,
        diagnosticItems: diagnosticsState.items,
        importItems: importHistory.items,
        importTotal: importHistory.total,
        reportItems: reportHistory.items,
        reportTotal: reportHistory.total,
        journalItems: journalHistory.items,
        journalTotal: journalHistory.total,
      });
      setLoadError("");
    } catch (error) {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setLoadError(error instanceof Error ? error.message : "Impossible de charger la chronologie des activités.");
      }
    } finally {
      if (!quiet && requestSequence === loadingRequestSequenceRef.current) {
        setIsLoading(false);
      }
    }
  }, [page]);

  React.useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let intervalId = null;
    const refreshQuietly = () => {
      if (isDocumentVisible()) {
        void loadHistory({ quiet: true });
      }
    };
    const startPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (isDocumentVisible()) {
        intervalId = window.setInterval(refreshQuietly, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      if (!isDocumentVisible()) {
        if (intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      refreshQuietly();
      startPollingInterval();
    };

    startPollingInterval();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [loadHistory]);

  const diagnosticItems = historyState?.diagnosticItems || EMPTY_ITEMS;
  const importItems = historyState?.importItems || EMPTY_ITEMS;
  const importKeys = React.useMemo(() => importItems.map(buildOrderImportCaptureKey), [importItems]);
  const importHighlightedKeys = useDispatchCaptureArrivalHighlight(importKeys, {
    enabled: historyState != null && historyState.page === page,
    resetKey: historyState?.page,
  });
  const reportItems = historyState?.reportItems || EMPTY_ITEMS;
  const reportKeys = React.useMemo(() => reportItems.map(buildReportActionCaptureKey), [reportItems]);
  const reportHighlightedKeys = useDispatchCaptureArrivalHighlight(reportKeys, {
    enabled: historyState != null && historyState.page === page,
    resetKey: page,
    onArrival: () => publishDispatchCaptureArrival("reports"),
    view: "reports",
  });
  const journalItems = historyState?.journalItems || EMPTY_ITEMS;
  const journalKeys = React.useMemo(() => journalItems.map(buildReportActionCaptureKey), [journalItems]);
  const journalHighlightedKeys = useDispatchCaptureArrivalHighlight(journalKeys, {
    enabled: historyState != null && historyState.page === page, resetKey: page,
    onArrival: () => publishDispatchCaptureArrival("journal"), view: "journal",
  });
  const keysOf = React.useCallback(
    (kind) => diagnosticItems.filter((item) => resolveDiagnosticActivityKind(item) === kind).map((item) => buildReassignmentCaptureKey(item)),
    [diagnosticItems],
  );
  const dispatchKeys = React.useMemo(() => keysOf("dispatch"), [keysOf]);
  const sourceChangesKeys = React.useMemo(() => keysOf("source_changes"), [keysOf]);
  const consolidationKeys = React.useMemo(() => keysOf("consolidation"), [keysOf]);
  const dispatchHighlightedKeys = useDispatchCaptureArrivalHighlight(dispatchKeys, {
    enabled: historyState != null,
    onArrival: () => publishDispatchCaptureArrival("reassignment"),
    view: "reassignment",
  });
  const sourceChangesHighlightedKeys = useDispatchCaptureArrivalHighlight(sourceChangesKeys, {
    enabled: historyState != null,
    onArrival: () => publishDispatchCaptureArrival("source_changes"),
    view: "source_changes",
  });
  const consolidationHighlightedKeys = useDispatchCaptureArrivalHighlight(consolidationKeys, {
    enabled: historyState != null,
    onArrival: () => publishDispatchCaptureArrival("consolidation"),
    view: "consolidation",
  });
  const diagnosticHighlightedKeys = { dispatch: dispatchHighlightedKeys, source_changes: sourceChangesHighlightedKeys,
    consolidation: consolidationHighlightedKeys };
  const combinedItems = React.useMemo(
    () => buildCombinedCaptureActivityItems(diagnosticItems, importItems, reportItems, journalItems),
    [diagnosticItems, importItems, reportItems, journalItems],
  );
  const total = diagnosticItems.length + (historyState?.importTotal || 0) + (historyState?.reportTotal || 0) + (historyState?.journalTotal || 0);
  const paginatedHistory = React.useMemo(
    () => paginateCombinedCaptureActivityItems(combinedItems, { page, total }),
    [combinedItems, page, total],
  );
  const groupedItems = React.useMemo(
    () => groupCaptureItemsByActivityDate(paginatedHistory.items, (activity) => activity?.occurredAt),
    [paginatedHistory.items],
  );

  return (
    <div
      aria-busy={isLoading}
      className="grid max-w-full min-h-min min-w-0 gap-3 overflow-x-clip"
      data-combined-capture-activity-history="true"
    >
      <section className="grid gap-2 rounded-[4px] border border-black/6 bg-white px-4 py-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
            statusToneClasses.success,
          ])}>
            Vue combinée
          </span>
          <span className="text-[0.8rem] font-medium text-slate-600">
            {historyState == null ? "Chargement de l’historique" : `${total} événement${total > 1 ? "s" : ""}`}
          </span>
        </div>
        <p className="m-0 text-[0.72rem] leading-5 text-slate-600">
          Activité Dispatch, Activités Mise à jour, Activités Consolidation, Imports commandes, Rapports et Journal, du plus récent au plus ancien.
        </p>
      </section>

      {loadError ? (
        <section className="grid gap-3 rounded-[4px] border border-rose-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]" role="alert">
          <p className="m-0 text-[0.9rem] font-semibold text-rose-700">{loadError}</p>
          <div>
            <button className={SECONDARY_ACTION_CLASS_NAME} onClick={() => void loadHistory()} type="button">
              <RefreshCcw size={15} aria-hidden="true" />
              Réessayer
            </button>
          </div>
        </section>
      ) : isLoading && historyState == null ? (
        <section className="flex min-h-52 items-center justify-center gap-3 rounded-[4px] border border-black/6 bg-white text-[0.86rem] font-medium text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />
          Chargement de la chronologie des activités…
        </section>
      ) : paginatedHistory.items.length > 0 ? (
        <section
          aria-label="Historique combiné des activités"
          className="grid max-w-full min-h-min min-w-0 gap-6 overflow-x-clip"
          data-combined-capture-activity-list="true"
        >
          {groupedItems.map((group) => (
            <div
              className="grid max-w-full min-h-min min-w-0 gap-2 overflow-x-clip rounded-[4px] border border-slate-200 bg-slate-50/90 p-2.5"
              data-combined-capture-activity-date-group="true"
              key={group.activityDate || "unspecified"}
            >
              <header className="flex items-center justify-between gap-3 rounded-[4px] border border-slate-300 bg-slate-200/90 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
                <div className="flex min-w-0 items-center gap-2 text-[0.95rem] font-extrabold tracking-[-0.02em] text-slate-950">
                  <CalendarDays className="shrink-0 text-slate-700" size={16} aria-hidden="true" />
                  <h3 className="m-0 truncate">{formatCaptureActivityGroupLabel(group.activityDate)}</h3>
                </div>
                <span className="shrink-0 rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-[0.68rem] font-bold text-slate-600">
                  {group.items.length} événement{group.items.length > 1 ? "s" : ""}
                </span>
              </header>
              {group.items.map((activity) => (
                <CombinedActivityCard
                  activity={activity}
                  diagnosticHighlightedKeys={diagnosticHighlightedKeys}
                  importHighlightedKeys={importHighlightedKeys}
                  key={activity.key}
                  reportHighlightedKeys={reportHighlightedKeys}
                  journalHighlightedKeys={journalHighlightedKeys}
                />
              ))}
            </div>
          ))}
        </section>
      ) : (
        <section className="grid min-h-52 place-items-center rounded-[4px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
          <div className="grid max-w-lg gap-2">
            <History className="mx-auto text-slate-400" size={30} aria-hidden="true" />
            <p className="m-0 text-[1rem] font-semibold text-slate-900">Aucune activité à afficher</p>
            <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
              Les activités Dispatch, les mises à jour, les gestes de Consolidation, les imports de commandes, les rapports et les confirmations du Journal apparaîtront ici lorsqu’ils seront enregistrés.
            </p>
          </div>
        </section>
      )}

      {paginatedHistory.total > 0 ? (
        <nav className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-black/6 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]" aria-label="Pagination des activités combinées">
          <span className="text-[0.76rem] font-medium text-slate-600">
            Page {paginatedHistory.currentPage} sur {paginatedHistory.pageCount} · {paginatedHistory.total} événement{paginatedHistory.total > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              className={SECONDARY_ACTION_CLASS_NAME}
              disabled={isLoading || paginatedHistory.currentPage <= 1}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              type="button"
            >
              Précédente
            </button>
            <button
              className={SECONDARY_ACTION_CLASS_NAME}
              disabled={isLoading || paginatedHistory.currentPage >= paginatedHistory.pageCount}
              onClick={() => setPage((currentPage) => currentPage + 1)}
              type="button"
            >
              Suivante
            </button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}
